import {
    Box,
    Button,
    Flex,
    Icon,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    useToast,
} from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { FaCloudUploadAlt } from 'react-icons/fa';

export interface ImportUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onFileSelected: (file: File) => void;
    onDownloadTemplate: () => void | Promise<void>;
    title?: string;
    accept?: string;
    supportedFormatsLabel?: string;
    maxSizeMB?: number;
    isSupportedFile?: (file: File) => boolean;
}

const DEFAULT_ACCEPT =
    '.csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

export function ImportUploadModal({
    isOpen,
    onClose,
    onFileSelected,
    onDownloadTemplate,
    title = 'Upload File',
    accept = DEFAULT_ACCEPT,
    supportedFormatsLabel = 'CSV, XLSX',
    maxSizeMB = 25,
    isSupportedFile,
}: ImportUploadModalProps) {
    const toast = useToast();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    const maxBytes = maxSizeMB * 1024 * 1024;

    const trySelectFile = (file: File | undefined | null) => {
        if (!file) return;

        if (isSupportedFile && !isSupportedFile(file)) {
            toast({
                title: 'Unsupported file type',
                description: `Supported formats: ${supportedFormatsLabel}`,
                status: 'warning',
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        if (file.size > maxBytes) {
            toast({
                title: 'File too large',
                description: `Maximum size is ${maxSizeMB} MB.`,
                status: 'warning',
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        onFileSelected(file);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        trySelectFile(file);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent borderRadius="xl">
                <ModalHeader fontSize="lg" fontWeight="bold" pb={2}>
                    {title}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={4}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={accept}
                        style={{ display: 'none' }}
                        onChange={handleInputChange}
                    />
                    <Flex
                        direction="column"
                        align="center"
                        justify="center"
                        border="2px dashed"
                        borderColor={isDragging ? 'teal.400' : 'teal.200'}
                        borderRadius="xl"
                        bg={isDragging ? 'teal.100' : '#EFF9FA'}
                        py={10}
                        px={4}
                        cursor="pointer"
                        onDragOver={(e) => {
                            e.preventDefault();
                            setIsDragging(true);
                        }}
                        onDragEnter={(e) => {
                            e.preventDefault();
                            setIsDragging(true);
                        }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={(e) => {
                            e.preventDefault();
                            setIsDragging(false);
                            trySelectFile(e.dataTransfer.files?.[0]);
                        }}
                        onClick={() => fileInputRef.current?.click()}
                        transition="all 0.15s"
                    >
                        <Flex
                            w="48px"
                            h="48px"
                            borderRadius="full"
                            bg="white"
                            align="center"
                            justify="center"
                            mb={3}
                            boxShadow="none"
                        >
                            <Icon
                                as={FaCloudUploadAlt as React.ElementType}
                                color="teal.400"
                                boxSize={5}
                            />
                        </Flex>
                        <Text fontSize="sm" color="gray.600">
                            Drag and drop file here or{' '}
                            <Text
                                as="span"
                                color="teal.500"
                                textDecoration="underline"
                                fontWeight="medium"
                            >
                                Choose file
                            </Text>
                        </Text>
                    </Flex>

                    <Flex justify="space-between" mt={3} px={1}>
                        <Text fontSize="xs" color="gray.500">
                            Supported formats: {supportedFormatsLabel}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                            Maximum size: {maxSizeMB} MB
                        </Text>
                    </Flex>

                    <Box borderWidth={1} borderColor="gray.200" borderRadius="lg" p={4} mt={5}>
                        <Flex align="center" justify="space-between">
                            <Flex align="center" gap={3}>
                                <Flex
                                    w="40px"
                                    h="40px"
                                    borderRadius="md"
                                    bg="green.50"
                                    align="center"
                                    justify="center"
                                    flexShrink={0}
                                >
                                    <Text
                                        fontSize="2xs"
                                        fontWeight="bold"
                                        color="green.500"
                                        letterSpacing="tight"
                                    >
                                        XLSX
                                    </Text>
                                </Flex>
                                <Box>
                                    <Text fontSize="sm" fontWeight="semibold">
                                        Template File
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                        Download the template and use it as a starting point.
                                    </Text>
                                </Box>
                            </Flex>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    void onDownloadTemplate();
                                }}
                                flexShrink={0}
                            >
                                Download
                            </Button>
                        </Flex>
                    </Box>
                </ModalBody>
                <ModalFooter borderTop="1px solid" borderColor="gray.300">
                    <Button size="action" variant="outlineGray" onClick={onClose}>
                        Cancel
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
